import { computed } from 'vue';
import { useChartTheme } from '@rdrive/theme';

/**
 * The colours the two spark cards (TotalSparkCard, TotalSparkCardWithSwitch) derive from their
 * props and the CURRENT theme — package-internal, not in the barrel. Lifted from
 * issue-analysis's TotalSparkCard*.vue, whose hex fallbacks are now `@rdrive/theme`'s
 * `chart.sparkline` tokens (`useChartTheme().value.sparkline`, light or dark).
 *
 *  - `computedSparklineColor`: `sparklineColor` when non-empty, else the mode's line colour
 *  - `computedGradient`: `sparklineGradient` when given, else the mode's area gradient
 *  - `chipColor`: the percentage chip — `primary` for null, `success` for >= 0, else `error`
 *  - `labelStyle(index)`: the inline style of week label `index` along the bottom — spread evenly
 *    over the card's width; the FIRST label is anchored at its left edge and the LAST at its right,
 *    so neither is clipped by the card (Daniel, 2026-10-07; before, every label was centred on its
 *    point, which cut about a third off the two end labels). A single label stays centred.
 *
 * Must be called from `setup()` inside an app that installed Vuetify (useChartTheme).
 * @param {{ sparklineColor?: string, sparklineGradient?: string[] | null, percentageChange?: number | null, sparklineLabels?: string[] }} props
 */
export function useSparkline(props) {
  const chart = useChartTheme();

  const computedSparklineColor = computed(() => props.sparklineColor || chart.value.sparkline.line);

  // Gradient goes from more opaque (top) to transparent (bottom)
  const computedGradient = computed(() => props.sparklineGradient || chart.value.sparkline.gradient);

  const chipColor = computed(() => {
    if (props.percentageChange === null) return 'primary';
    return props.percentageChange >= 0 ? 'success' : 'error';
  });

  const labelStyle = (index) => {
    const n = props.sparklineLabels?.length ?? 0;
    if (n <= 1) return 'font-size: 10px; left: 50%; transform: translateX(-50%);';
    const shift = index === 0 ? '0' : index === n - 1 ? '-100%' : '-50%';
    return `font-size: 10px; left: ${(index / (n - 1)) * 100}%; transform: translateX(${shift});`;
  };

  return { computedSparklineColor, computedGradient, chipColor, labelStyle };
}
